/**
 * GrantOS UI — wspólne atomy (design system).
 *
 * Cel (audyt 2026): zastąpić powtarzalny inline-styled kod (Card ~200 stron, Button ~400,
 * natywne confirm() 648×, rozjechane gradienty 149 plików) spójnymi komponentami
 * w palecie navy/gold. Import: `import { Button, Card, PageHeader, useConfirm } from '@/components/ui';`
 */
import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

// ─────────────────────────── Button ───────────────────────────
type BtnVariant = 'primary' | 'gold' | 'secondary' | 'danger' | 'ghost' | 'success';
type BtnSize = 'sm' | 'md' | 'lg';
const BTN_VARIANT: Record<BtnVariant, string> = {
  primary: 'bg-navy-800 hover:bg-navy-900 text-white',
  gold: 'bg-gold-500 hover:bg-gold-600 text-white shadow-sm',
  secondary: 'bg-white hover:bg-navy-50 text-navy-800 border border-navy-200',
  danger: 'bg-red-600 hover:bg-red-700 text-white',
  success: 'bg-emerald-600 hover:bg-emerald-700 text-white',
  ghost: 'bg-transparent hover:bg-navy-50 text-navy-700',
};
const BTN_SIZE: Record<BtnSize, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};
export function Button({
  variant = 'primary', size = 'md', loading, icon, children, className = '', ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant; size?: BtnSize; loading?: boolean; icon?: ReactNode }) {
  return (
    <button
      {...props}
      disabled={props.disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${BTN_VARIANT[variant]} ${BTN_SIZE[size]} ${className}`}
    >
      {loading ? <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : icon}
      {children}
    </button>
  );
}

// ─────────────────────────── Card ───────────────────────────
export function Card({ children, className = '', padding = true }: { children: ReactNode; className?: string; padding?: boolean }) {
  return <div className={`bg-white rounded-xl shadow-sm border border-navy-100 ${padding ? 'p-5' : ''} ${className}`}>{children}</div>;
}

// ─────────────────────────── StatCard ───────────────────────────
export function StatCard({ label, value, sub, icon, color = 'navy' }: {
  label: string; value: ReactNode; sub?: string; icon?: ReactNode;
  color?: 'navy' | 'gold' | 'emerald' | 'red' | 'indigo';
}) {
  const accent: Record<string, string> = {
    navy: 'text-navy-800', gold: 'text-gold-600', emerald: 'text-emerald-600', red: 'text-red-600', indigo: 'text-indigo-600',
  };
  return (
    <Card className="flex items-center justify-between">
      <div>
        <div className="text-xs font-medium text-navy-400 uppercase tracking-wide">{label}</div>
        <div className={`text-2xl font-bold mt-1 ${accent[color]}`}>{value}</div>
        {sub && <div className="text-xs text-navy-400 mt-1">{sub}</div>}
      </div>
      {icon && <div className={`text-3xl opacity-80 ${accent[color]}`}>{icon}</div>}
    </Card>
  );
}

// ─────────────────────────── PageHeader ───────────────────────────
export function PageHeader({ title, subtitle, icon, actions }: {
  title: string; subtitle?: string; icon?: ReactNode; actions?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6">
      <div className="flex items-start gap-3">
        {icon && <div className="text-gold-500 text-3xl mt-0.5">{icon}</div>}
        <div>
          <h1 className="text-2xl font-extrabold text-navy-900 tracking-tight">{title}</h1>
          {subtitle && <p className="text-sm text-navy-400 mt-1">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
    </div>
  );
}

// ─────────────────────────── Badge ───────────────────────────
export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'gold' }) {
  const tones: Record<string, string> = {
    neutral: 'bg-navy-100 text-navy-700', success: 'bg-emerald-100 text-emerald-700',
    warning: 'bg-amber-100 text-amber-700', danger: 'bg-red-100 text-red-700',
    info: 'bg-indigo-100 text-indigo-700', gold: 'bg-gold-100 text-gold-700',
  };
  return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${tones[tone]}`}>{children}</span>;
}

// ─────────────────────────── EmptyState ───────────────────────────
export function EmptyState({ icon = '📭', title, hint }: { icon?: ReactNode; title: string; hint?: string }) {
  return (
    <div className="text-center py-12 text-navy-300">
      <div className="text-4xl mb-2">{icon}</div>
      <div className="font-medium text-navy-500">{title}</div>
      {hint && <div className="text-sm text-navy-300 mt-1">{hint}</div>}
    </div>
  );
}

// ─────────────────────────── DataTable ───────────────────────────
export interface Column<T> { key: string; header: string; render?: (row: T) => ReactNode; className?: string; }
export function DataTable<T extends Record<string, any>>({ columns, rows, keyField = 'id', empty, loading }: {
  columns: Column<T>[]; rows: T[]; keyField?: string; empty?: ReactNode; loading?: boolean;
}) {
  if (loading) return <div className="py-12 text-center text-navy-300"><span className="inline-block w-6 h-6 border-2 border-navy-300 border-t-transparent rounded-full animate-spin" /></div>;
  if (!rows.length) return <>{empty || <EmptyState title="Brak danych" />}</>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-navy-50 border-b-2 border-navy-100">
            {columns.map(c => <th key={c.key} className={`text-left px-3 py-2.5 font-bold text-navy-800 text-xs ${c.className || ''}`}>{c.header}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row[keyField] ?? i} className="border-b border-navy-50 hover:bg-gold-50/40 transition-colors">
              {columns.map(c => <td key={c.key} className={`px-3 py-2.5 ${c.className || ''}`}>{c.render ? c.render(row) : row[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────── ConfirmDialog + useConfirm ───────────────────────────
interface ConfirmOpts { title: string; message?: string; confirmLabel?: string; cancelLabel?: string; danger?: boolean; }
const ConfirmCtx = createContext<(opts: ConfirmOpts) => Promise<boolean>>(async () => false);
export const useConfirm = () => useContext(ConfirmCtx);

// Imperatywny wariant (bez hooka) — dla handlerów/callbacków poza ciałem komponentu.
// ConfirmProvider rejestruje żywy handler na mount; fallback = natywny confirm() gdy provider niezamontowany.
let _confirmHandler: ((opts: ConfirmOpts) => Promise<boolean>) | null = null;
export function askConfirm(opts: ConfirmOpts): Promise<boolean> {
  if (_confirmHandler) return _confirmHandler(opts);
  return Promise.resolve(window.confirm(opts.title + (opts.message ? '\n' + opts.message : '')));
}

// Imperatywny prompt (input-modal) — semantyka jak window.prompt: Promise<string|null> (null = anulowano).
interface PromptOpts { title: string; message?: string; defaultValue?: string; placeholder?: string; confirmLabel?: string; cancelLabel?: string; multiline?: boolean; }
let _promptHandler: ((opts: PromptOpts) => Promise<string | null>) | null = null;
export function askPrompt(opts: PromptOpts): Promise<string | null> {
  if (_promptHandler) return _promptHandler(opts);
  return Promise.resolve(window.prompt(opts.title + (opts.message ? '\n' + opts.message : ''), opts.defaultValue ?? ''));
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ opts: ConfirmOpts; resolve: (v: boolean) => void } | null>(null);
  const [pState, setPState] = useState<{ opts: PromptOpts; resolve: (v: string | null) => void; value: string } | null>(null);
  const confirm = useCallback((opts: ConfirmOpts) => new Promise<boolean>(resolve => setState({ opts, resolve })), []);
  const promptFn = useCallback((opts: PromptOpts) => new Promise<string | null>(resolve => setPState({ opts, resolve, value: opts.defaultValue ?? '' })), []);
  useEffect(() => { _confirmHandler = confirm; return () => { _confirmHandler = null; }; }, [confirm]);
  useEffect(() => { _promptHandler = promptFn; return () => { _promptHandler = null; }; }, [promptFn]);
  const close = (v: boolean) => { state?.resolve(v); setState(null); };
  const closePrompt = (ok: boolean) => { if (!pState) return; pState.resolve(ok ? pState.value : null); setPState(null); };
  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-navy-950/50 p-4" onClick={() => close(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-navy-900">{state.opts.title}</h3>
            {state.opts.message && <p className="text-sm text-navy-500 mt-2">{state.opts.message}</p>}
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="secondary" onClick={() => close(false)}>{state.opts.cancelLabel || 'Anuluj'}</Button>
              <Button variant={state.opts.danger ? 'danger' : 'primary'} onClick={() => close(true)}>{state.opts.confirmLabel || 'Potwierdź'}</Button>
            </div>
          </div>
        </div>
      )}
      {pState && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-navy-950/50 p-4" onClick={() => closePrompt(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-navy-900">{pState.opts.title}</h3>
            {pState.opts.message && <p className="text-sm text-navy-500 mt-2">{pState.opts.message}</p>}
            {pState.opts.multiline ? (
              <textarea
                autoFocus rows={4} value={pState.value} placeholder={pState.opts.placeholder}
                onChange={e => setPState(s => s ? { ...s, value: e.target.value } : s)}
                onKeyDown={e => { if (e.key === 'Escape') closePrompt(false); }}
                className="mt-4 px-3 py-2 border border-navy-200 rounded-lg text-sm w-full focus:outline-none focus:ring-2 focus:ring-gold-300 focus:border-gold-400"
              />
            ) : (
              <input
                autoFocus type="text" value={pState.value} placeholder={pState.opts.placeholder}
                onChange={e => setPState(s => s ? { ...s, value: e.target.value } : s)}
                onKeyDown={e => { if (e.key === 'Enter') closePrompt(true); if (e.key === 'Escape') closePrompt(false); }}
                className="mt-4 px-3 py-2 border border-navy-200 rounded-lg text-sm w-full focus:outline-none focus:ring-2 focus:ring-gold-300 focus:border-gold-400"
              />
            )}
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="secondary" onClick={() => closePrompt(false)}>{pState.opts.cancelLabel || 'Anuluj'}</Button>
              <Button variant="primary" onClick={() => closePrompt(true)}>{pState.opts.confirmLabel || 'OK'}</Button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}

// ─────────────────────────── Input / Field ───────────────────────────
export function Input({ className = '', ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`px-3 py-2 border border-navy-200 rounded-lg text-sm w-full focus:outline-none focus:ring-2 focus:ring-gold-300 focus:border-gold-400 ${className}`} />;
}
export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-navy-600 mb-1 block">{label}</span>
      {children}
      {hint && <span className="text-xs text-navy-400 mt-1 block">{hint}</span>}
    </label>
  );
}
